<script setup>
import axios from 'axios';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import XlSkeleton from '../../xl/XlSkeleton.vue';
import BoxContent from '../../../pge-components/pge-content-block.vue';
import XlCard from '../../xl/XlCard.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';
import XlSwitch from '../../xl/XlSwitch.vue';
import XlTabs from '../../xl/XlTabs.vue';
import BannersTab from './BannersTab.vue';
import FilesTab from './FilesTab.vue';
import LinksTab from './LinksTab.vue';
import { useCreateOnArrival } from '../../../composables/useCreateOnArrival';

const bannersEl = ref(null);
const linksEl = ref(null);
const resourcesEl = ref(null);

const route = useRoute();
const router = useRouter();

// Selected via ?tab=, so the Overview's "Post a banner" and "Add a resource"
// tiles can land on the right one.
const TAB_IDS = ['resources', 'banners', 'links'];
const DEFAULT_TAB = 'resources';
const activeTab = computed(() => (TAB_IDS.includes(route.query.tab) ? route.query.tab : DEFAULT_TAB));

// A full-page editor takes the whole page, so the tabs and the block switch
// stand down while it is open.
const editing = reactive({ banners: false, links: false });
const anyEditing = computed(() => editing.banners || editing.links);

const selectTab = (tabId) => {
  if (tabId === activeTab.value) return;
  // replace, not push: a tab is not a place you go back to, and any other
  // query already in the URL has to survive the write.
  router.replace({ path: route.path, query: { ...route.query, tab: tabId } });
};

/**
 * The Overview's two Content tiles. The composable records WHAT was asked
 * for; the watcher below does the acting, once the panel has rendered.
 */
const pendingCreate = ref(null);

const { cancelReturn: returnFromBannerTile } = useCreateOnArrival('banner', () => {
  pendingCreate.value = 'banner';
});
useCreateOnArrival('resource', () => { pendingCreate.value = 'resource'; });

const isLoading = ref(true);

watch(
  [pendingCreate, isLoading, bannersEl, resourcesEl],
  () => {
    if (!pendingCreate.value || isLoading.value) return;

    const acted = pendingCreate.value === 'banner'
      ? bannersEl.value?.openAdd()
      : resourcesEl.value?.focusAddResource();

    if (acted === true) pendingCreate.value = null;
  },
  { flush: 'post' }
);

// One block per tab: whether the policyholder sees it at all.
const BLOCKS = {
  resources: { key: 'event_box_resources', noun: 'Files', where: "They appear in the policyholder's Library" },
  banners: { key: 'event_box_banners', noun: 'Banners', where: "Shown to policyholders in the portal" },
  // Home's Quick links (XN-4691) are the one place the portal shows them.
  links: { key: 'event_box_links', noun: 'Links', where: "They appear on the policyholder's Home" },
};

const visible = reactive({ resources: false, banners: false, links: false });
// null until the tab has counted: its pill holds a digit's place meanwhile.
const counts = reactive({ resources: null, banners: null, links: null });
const countOf = (n) => n ?? '\u2007';
const bannersFeatureEnabled = ref(false);

const block = computed(() => BLOCKS[activeTab.value]);

const failed = reactive({ banners: false, links: false });
const ASSUME_SHARED = 1;

const otherShared = computed(() => {
  if (isLoading.value) return null;
  const sources = bannersFeatureEnabled.value ? ['links', 'banners'] : ['links'];
  if (sources.some((tab) => failed[tab])) return ASSUME_SHARED;
  if (sources.some((tab) => counts[tab] === null)) return null;
  return sources.reduce((sum, tab) => sum + counts[tab], 0);
});

const tabs = computed(() => [
  { id: 'resources', label: 'Files and guides', count: countOf(counts.resources) },
  ...(bannersFeatureEnabled.value ? [{ id: 'banners', label: 'Banners', count: countOf(counts.banners) }] : []),
  { id: 'links', label: 'Links', count: countOf(counts.links) },
]);

const setVisible = async (tab, on) => {
  visible[tab] = on;
  try {
    const res = await axios.post(`${window.store.event_backend_api}/event-settings-all`, { [BLOCKS[tab].key]: on ? 1 : 0 });
    if (res.data && res.data.message) {
      window.loadNotification({ message: 'Hub page was updated!' });
    }
  } catch (error) {
    // The switch must not lie: put it back to what the server still holds.
    visible[tab] = !on;
    console.error(error);
    window.loadNotification({ message: `Could not ${on ? 'show' : 'hide'} ${BLOCKS[tab].noun.toLowerCase()}. Try again.`, error: true });
  }
};

const getEventSettings = async () => {
  try {
    const response = await axios.get(`${window.store.event_backend_api}/event-settings`, {
      params: { fields: 'event_box_links,event_box_resources,event_box_banners' },
    });

    if (response.data) {
      visible.resources = !!Number.parseInt(response.data.settings?.event_box_resources);
      visible.links = !!Number.parseInt(response.data.settings?.event_box_links);
      visible.banners = !!Number.parseInt(response.data.settings?.event_box_banners);
      bannersFeatureEnabled.value = !!response.data.pinned_banners_enabled;
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await getEventSettings();
});
</script>

<template>
  <div class="content-page" data-xl-gen="northstar">
    <box-content
      title="Resources & Content"
      subtitle="Everything you put in front of policyholders — and who each piece is for."
      transparent
    >
      <!--
        Drawn at once: the tabs, the card that says whether this block is on,
        and the tab's own list, which is the one thing with a skeleton. A page
        skeleton used to stand in for the settings and then give way to the
        tabs and a second skeleton — two loaders, and a jump between them.
      -->
        <xl-tabs v-if="!anyEditing" :tabs="tabs" :model-value="activeTab" @update:model-value="selectTab" />

        <div class="content-page__panel">
          <xl-card v-if="!anyEditing" padded class="content-page__block" data-testid="block-toggle">
            <XlSkeleton v-if="isLoading" width="44px" height="44px" radius="13px" class="content-page__sk" />
            <xl-status-icon v-else :tone="visible[activeTab] ? 'success' : 'warning'">
              <svg v-if="visible[activeTab]" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5.8 12.3 4.2 4.2L18.2 7.8" /></svg>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4.4 3.6 19.6h16.8z" /><path d="M12 10v3.8M12 16.8h.01" /></svg>
            </xl-status-icon>
            <div v-if="isLoading" class="content-page__block-text" data-testid="block-toggle-loading">
              <b><XlSkeleton width="180px" height="0.85em" radius="6px" class="content-page__sk" /></b>
              <span><XlSkeleton width="260px" height="0.8em" radius="6px" class="content-page__sk" /></span>
            </div>
            <div v-else class="content-page__block-text">
              <b>{{ block.noun }} are {{ visible[activeTab] ? 'switched on' : 'hidden' }}</b>
              <span>{{ visible[activeTab] ? block.where : 'Nothing here is visible to anybody at the moment.' }}</span>
            </div>
            <XlSkeleton v-if="isLoading" width="44px" height="24px" radius="99px" class="content-page__sk" />
            <xl-switch
              v-else
              :model-value="visible[activeTab]"
              :name="block.key"
              :aria-label="`${visible[activeTab] ? 'Hide' : 'Show'} ${block.noun.toLowerCase()} on the hub`"
              @update:model-value="(on) => setVisible(activeTab, on)"
            />
          </xl-card>

          <div v-show="activeTab === 'resources' && !anyEditing">
            <files-tab
              ref="resourcesEl"
              :actions-disabled="!visible.resources"
              :other-shared="otherShared"
              @count="(n) => { counts.resources = n; }"
            />
          </div>

          <div v-if="bannersFeatureEnabled" v-show="activeTab === 'banners'">
            <banners-tab
              ref="bannersEl"
              :actions-disabled="false"
              :block-on="visible.banners"
              @count="(n) => { counts.banners = n; }"
              @failed="(on) => { failed.banners = on; }"
              @editing="(on) => { editing.banners = on; }"
              @banner-saved="getEventSettings"
              @modal-closed="returnFromBannerTile"
            />
          </div>

          <div v-show="activeTab === 'links'">
            <links-tab
              ref="linksEl"
              :actions-disabled="!visible.links"
              @count="(n) => { counts.links = n; }"
              @failed="(on) => { failed.links = on; }"
              @editing="(on) => { editing.links = on; }"
            />
          </div>
        </div>
    </box-content>
  </div>
</template>

<style scoped lang="scss">
.content-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  font-family: var(--xl-font-family);

  &__panel {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__block {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 0;
  }

  /* The page is not one white panel: a heading, then cards on the paper
     behind them. The pge block still supplies the markup — these rules only
     stop it painting a card around the lot. */
  background: var(--xl-color-background, #fcfafd);

  /* The pge block is the page here, so it takes the shared page gutter. */
  :deep(.pge-content-block) { padding: var(--setup-page-padding) var(--setup-page-padding) 24px; gap: 24px; }
  :deep(.pge-cb-h-container) { gap: 6px; }
  :deep(.pge-cb-h-line) { display: none; }

  :deep(.pge-cb-h-title) {
    font-family: var(--xl-font-family);
    font-size: 30px;
    font-weight: 600;
    line-height: 1.2;
    color: var(--xl-color-primary-text, #1c1720);
  }

  :deep(.pge-cb-h-subtitle span) {
    font-family: var(--xl-font-family);
    font-size: var(--xl-page-subtitle-size);
    line-height: var(--xl-page-subtitle-line);
    letter-spacing: 0;
    color: var(--xl-color-outline);
  }

  :deep(.pge-cb-content) { padding-top: 20px; }

  &__sk { width: auto; flex: 0 0 auto; vertical-align: middle; }

  &__block-text {
    flex: 1;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    b {
      font-size: var(--xl-type-body-large-size);
      color: var(--xl-color-primary-text);
    }

    span {
      font-size: var(--xl-type-body-medium-size);
      color: var(--xl-color-secondary-text);
    }
  }
}
</style>
